/*
  PREVIEW ONLY — Glassy Dashboard style inspired by SugarCRM reference.
  Route: /preview/glassy-dashboard  (not linked from anywhere; open manually)
  Fully self-contained. No API calls; uses mock numbers matching current data.
  DO NOT import Layout — this preview has its own full-page shell.
*/
import { Sparkles, Search, Bell, LayoutDashboard, ListChecks, Users as UsersIcon, Building2, BarChart3, Target, Settings2, Sun, Moon, Share2, Upload, Star, Plus, Smartphone, Database, Calendar, Send, TriangleAlert, MoreHorizontal, Clock, Filter, ChevronRight, Trophy, Wallet, TrendingUp } from "lucide-react";

/* ---------- Reusable "glass" primitives ---------- */
const glass = "backdrop-blur-2xl bg-white/60 border border-white/60 shadow-[0_4px_14px_rgba(30,16,80,0.04),0_24px_50px_-20px_rgba(30,16,80,0.07)]";
const glassSoft = "backdrop-blur-xl bg-white/45 border border-white/50";
const pillActive = "bg-[#090808] text-white shadow-[0_6px_16px_rgba(15,15,23,0.28)]";
const pillIdle = "text-[#626061] hover:bg-white/50";
const chip = "inline-flex items-center h-9 px-4 rounded-full text-[13px] font-semibold transition-colors";

/* ---------- Mock data (mirrors current Dashboard layout) ---------- */
const KPIS = [
  { label: "Total Target",       value: "Rp 25.000.000.000", sub: "Tahun 2026",              icon: Target,     tint: "text-[#626061]" },
  { label: "Total Achievement",  value: "Rp 9.638.370.400",  sub: "3 deal closed",            icon: Wallet,     tint: "text-[#57D28A]" },
  { label: "% Achievement Tim",  value: "38.6%",              sub: "Sisa Rp 15.361.629.600",   icon: Trophy,     tint: "text-[#FFDB52]" },
];
const OVERDUE = [
  { name: "Kemensos",   svc: "Pengadaan UPS",           owner: "Yoga", days: 10, hue: "bg-[#FF6B7D]" },
  { name: "WYTO",       svc: "Hardware dan Smart App",  owner: "Iin",  days: 11, hue: "bg-[#FFDB52]" },
  { name: "Agen Sakti", svc: "Digitalisasi Museum",     owner: "Yoga", days: 10, hue: "bg-[#97ADD4]" },
];
const RANKING = [
  { rank: 1, name: "Yoga",     nickname: "Prayoga Pangestu",  achievement: "Rp 5,4 M",   pct: 52, tier: "amber" },
  { rank: 2, name: "Iin",      nickname: "Sri Aini",          achievement: "Rp 2,8 M",   pct: 28, tier: "slate" },
  { rank: 3, name: "Dina",     nickname: "Dina Marlina",      achievement: "Rp 1,3 M",   pct: 13, tier: "orange" },
];

/* ---------- Layout ---------- */
export default function PreviewGlassyDashboard() {
  return (
    <div className="min-h-screen relative overflow-hidden font-sans" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif" }}>
      {/* Ambient gradient background */}
      <div className="absolute inset-0 -z-10 bg-[#F4F1EC]" />
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-violet-300/50 via-fuchsia-200/40 to-transparent blur-3xl -z-10" />
      <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-sky-200/50 via-emerald-100/40 to-transparent blur-3xl -z-10" />
      <div className="absolute bottom-0 left-1/2 w-[500px] h-[500px] -translate-x-1/2 rounded-full bg-gradient-to-t from-amber-100/50 to-transparent blur-3xl -z-10" />

      <div className="flex">
        {/* -------- Slim icon sidebar -------- */}
        <aside className={`w-[72px] shrink-0 h-screen sticky top-0 flex flex-col items-center py-4 gap-1.5 ${glassSoft} border-r-white/40`}>
          <button className="w-10 h-10 rounded-2xl bg-white/60 flex items-center justify-center text-[#090808] hover:bg-white/80 transition"><ChevronRight className="w-4 h-4 rotate-180" /></button>
          <div className="my-3 h-px w-8 bg-black/10" />
          {[Share2, Upload, Star, Plus, Smartphone, Database, Calendar, Send, TriangleAlert].map((Icon, i) => (
            <button key={i} className="relative w-10 h-10 rounded-2xl flex items-center justify-center text-[#626061] hover:bg-white/60 transition">
              <Icon className="w-[18px] h-[18px]" strokeWidth={1.6} />
              {Icon === TriangleAlert && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#FF6B7D]" />}
            </button>
          ))}
          <div className="mt-auto flex flex-col gap-2">
            <button className="w-10 h-10 rounded-full bg-[#090808] text-white flex items-center justify-center"><Moon className="w-4 h-4" strokeWidth={1.7} /></button>
            <button className="w-10 h-10 rounded-full bg-white/60 flex items-center justify-center text-[#090808]"><Sun className="w-4 h-4" strokeWidth={1.7} /></button>
          </div>
        </aside>

        {/* -------- Main -------- */}
        <div className="flex-1 min-w-0">
          {/* Top nav */}
          <header className="flex items-center justify-between px-10 pt-6 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-[#090808] text-white flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
              <div className="text-[19px] font-bold tracking-tight text-[#090808]">super<span className="font-normal">sales</span></div>
            </div>
            <nav className="flex items-center gap-1">
              {[
                { label: "Dashboard", active: true },
                { label: "Prospek" },
                { label: "Kanban" },
                { label: "Client" },
                { label: "Laporan" },
                { label: "Rapor" },
              ].map((n) => (
                <button key={n.label} className={`${chip} ${n.active ? pillActive : pillIdle}`}>{n.label}</button>
              ))}
            </nav>
            <div className="flex items-center gap-2">
              <button className="w-11 h-11 rounded-full bg-white/60 backdrop-blur flex items-center justify-center text-[#090808] hover:bg-white/80 transition"><Search className="w-4 h-4" strokeWidth={1.8} /></button>
              <button className="w-11 h-11 rounded-full bg-white/60 backdrop-blur flex items-center justify-center text-[#090808] relative"><Bell className="w-4 h-4" strokeWidth={1.8} /><span className="absolute top-2.5 right-3 w-1.5 h-1.5 rounded-full bg-[#FF6B7D]" /></button>
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-400 to-pink-400 flex items-center justify-center text-white text-sm font-bold ring-2 ring-white">O</div>
            </div>
          </header>

          {/* Page content */}
          <div className="px-10 pb-10 pt-2 space-y-6">
            {/* Title */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.24em] text-[#626061]/70 font-semibold mb-1.5">DASHBOARD · 2026</div>
              <h1 className="text-[44px] leading-none font-bold tracking-tight text-[#090808]">Halo, Owner 👋</h1>
              <p className="text-sm text-[#626061]/70 mt-2">Ringkasan target vs pencapaian seluruh tim</p>
            </div>

            {/* Follow-up glass card */}
            <div className={`${glass} rounded-[32px] p-6`}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-2xl bg-[#FFDB52]/90 text-white flex items-center justify-center"><Clock className="w-4 h-4" /></div>
                <div>
                  <div className="text-[17px] font-bold text-[#090808]">Perlu Follow-up</div>
                  <div className="text-[11px] text-[#626061]/60 font-medium">3 prospek · H+7</div>
                </div>
                <button className={`${chip} ml-auto bg-white/70 hover:bg-white text-[#090808]`}><Filter className="w-3.5 h-3.5" /> Semua</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {OVERDUE.map((o) => (
                  <div key={o.name} className={`${glassSoft} rounded-full px-3 py-2.5 flex items-center gap-3 hover:bg-white/70 transition`}>
                    <div className={`w-8 h-8 rounded-full ${o.hue} text-white flex items-center justify-center text-[11px] font-bold`}>{o.name[0]}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] text-[#090808] font-semibold truncate">{o.name} <span className="text-[#626061]/50 font-normal text-[11px]">· {o.svc}</span></div>
                      <div className="text-[10px] text-[#626061]/60">{o.owner}</div>
                    </div>
                    <span className="text-[11px] font-bold text-[#B39548] tabular-nums">{o.days}h</span>
                  </div>
                ))}
              </div>
            </div>

            {/* KPI trio */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {KPIS.map((k) => (
                <div key={k.label} className={`${glass} rounded-[28px] p-6`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#626061]/70">{k.label}</span>
                    <div className={`w-10 h-10 rounded-full ${glassSoft} flex items-center justify-center ${k.tint}`}><k.icon className="w-[18px] h-[18px]" strokeWidth={1.8} /></div>
                  </div>
                  <div className="text-[24px] font-bold text-[#090808] tracking-tight" style={{ fontFamily: "'JetBrains Mono',monospace" }}>{k.value}</div>
                  <div className="text-[11px] text-[#626061]/60 mt-1.5">{k.sub}</div>
                </div>
              ))}
            </div>

            {/* Progress + chart row */}
            <div className={`${glass} rounded-[32px] p-7`}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="text-[17px] font-bold text-[#090808]">Progress Tim Tahun 2026</div>
                  <div className="text-[11px] text-[#626061]/60 mt-0.5">Rp 9.638.370.400 dari Rp 25.000.000.000</div>
                </div>
                <div className="flex gap-2">
                  <span className={`${chip} ${pillActive}`}>YTD</span>
                  <span className={`${chip} bg-white/60 text-[#090808]`}>Bulan ini</span>
                  <span className={`${chip} bg-white/60 text-[#090808]`}>Custom</span>
                </div>
              </div>
              <div className="relative h-3 rounded-full bg-black/5 overflow-hidden">
                <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#090808] to-[#626061]" style={{ width: "38.6%" }} />
              </div>

              {/* Mini bar chart mock */}
              <div className="mt-8 grid grid-cols-12 items-end gap-2 h-32">
                {[8, 22, 14, 40, 20, 68, 90, 62, 44, 24, 12, 8].map((h, i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5">
                    <div className="w-full rounded-t-[10px] bg-gradient-to-t from-[#090808] to-[#626061]" style={{ height: `${h}%` }} />
                    <div className="text-[9px] uppercase tracking-widest text-[#626061]/60 font-semibold">{["J","F","M","A","M","J","J","A","S","O","N","D"][i]}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Ranking */}
            <div className={`${glass} rounded-[32px] p-6`}>
              <div className="flex items-center justify-between mb-5">
                <div className="text-[17px] font-bold text-[#090808]">Ranking Sales</div>
                <span className={`${chip} bg-white/60 text-[#090808]`}>Semua Sales</span>
              </div>
              <div className="space-y-3">
                {RANKING.map((s) => (
                  <div key={s.rank} className={`${glassSoft} rounded-full px-3 py-2.5 flex items-center gap-4 hover:bg-white/70 transition`}>
                    <div className={`w-8 h-8 rounded-full font-bold text-[11px] flex items-center justify-center ${
                      s.tier === "amber" ? "bg-[#FFE89A] text-[#7A6633]" : s.tier === "slate" ? "bg-[#B9C5D8] text-[#626061]" : "bg-[#FFD7B5] text-[#7A5533]"
                    }`}>#{s.rank}</div>
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-300 to-pink-300 text-white flex items-center justify-center text-[13px] font-bold">{s.name[0]}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-bold text-[#090808]">{s.name} <span className="font-normal text-[#626061]/60 text-[11px]">· {s.nickname}</span></div>
                      <div className="relative h-1.5 bg-black/5 rounded-full mt-1.5 overflow-hidden">
                        <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-violet-500 to-pink-500" style={{ width: `${s.pct}%` }} />
                      </div>
                    </div>
                    <div className="text-right shrink-0 pr-2">
                      <div className="text-[13px] font-bold text-[#090808] tabular-nums font-num">{s.achievement}</div>
                      <div className="text-[10px] text-[#626061]/60">{s.pct}% dari target</div>
                    </div>
                    <button className="w-8 h-8 rounded-full bg-white/70 hover:bg-white flex items-center justify-center"><MoreHorizontal className="w-4 h-4 text-[#626061]" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
